import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, oauthStartUrl, setToken } from '../api';
import { OAUTH_SCHEME_RETURN, isNative, openAuthUrl } from '../native';

type View = 'home' | 'register' | 'login';

/**
 * Sign-in. The first screen fits a phone without scrolling: logo, social
 * buttons, and two choices. Create account / Log in each open their own
 * screen with just the form. OAuth returns here as /login#token=… (or #error=…).
 */
export function LoginPage() {
  const [view, setView] = useState<View>('home');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.providers().then((r) => setProviders(r.providers)).catch(() => {});
    const frag = new URLSearchParams(loc.hash.replace(/^#/, ''));
    if (frag.get('token')) {
      setToken(frag.get('token'));
      nav(frag.get('created') === '1' ? '/profile' : '/searches', { replace: true });
    } else if (frag.get('error')) {
      setError(frag.get('error'));
    }
  }, [loc.hash, nav]);

  const social = (p: string) => openAuthUrl(oauthStartUrl(p, 'login', isNative() ? OAUTH_SCHEME_RETURN : undefined));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = view === 'register' ? await api.register(email, password, name) : await api.loginEmail(email, password);
      setToken(r.token);
      nav(r.created ? '/profile' : '/searches', { replace: true });
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  if (view === 'home') {
    return (
      <div className="login">
        <div className="login-hero">
          <img className="logo-img" src="/icon-192.png" alt="" width={96} height={96} />
          <h1>Bandwagn</h1>
          <p className="muted">Find musicians near you who like the same bands.</p>
        </div>
        <div className="stack login-choices">
          {providers.includes('google') && <button type="button" className="btn block social google" onClick={() => social('google')}>Continue with Google</button>}
          {providers.includes('facebook') && <button type="button" className="btn block social facebook" onClick={() => social('facebook')}>Continue with Facebook</button>}
          {providers.length > 0 && <div className="divider"><span>or</span></div>}
          <button type="button" className="btn block" onClick={() => { setError(null); setView('register'); }}>Create account</button>
          <button type="button" className="btn block secondary" onClick={() => { setError(null); setView('login'); }}>Log in with email</button>
          {error && <div className="error" style={{ textAlign: 'center' }}>{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="row" style={{ marginBottom: 8 }}>
        <button type="button" className="btn ghost" onClick={() => { setError(null); setView('home'); }}>‹ Back</button>
      </div>
      <h1 style={{ margin: '0 0 16px' }}>{view === 'register' ? 'Create your account' : 'Welcome back'}</h1>
      <form className="card stack" onSubmit={submit}>
        {view === 'register' && (
          <label className="field"><span>Your name</span><input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoComplete="name" placeholder="How other musicians will see you" autoFocus /></label>
        )}
        <label className="field"><span>Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" inputMode="email" autoFocus={view === 'login'} /></label>
        <label className="field"><span>Password</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete={view === 'register' ? 'new-password' : 'current-password'} placeholder={view === 'register' ? 'At least 8 characters' : ''} /></label>
        {error && <div className="error">{error}</div>}
        <button className="btn block" type="submit" disabled={busy}>{busy ? 'One moment…' : view === 'register' ? 'Create account' : 'Log in'}</button>
        <button type="button" className="btn ghost sm" onClick={() => { setError(null); setView(view === 'register' ? 'login' : 'register'); }}>
          {view === 'register' ? 'Already have an account? Log in' : 'New here? Create an account'}
        </button>
      </form>
    </div>
  );
}
